// Quadro do pipeline de licitações — uma coluna por etapa do processo público.
//
// Copiado do desenho do pipeline privado (KanbanPipeline, 01/10/2026): o
// cabeçalho da coluna é a seta do funil (nome, quantidade, valor curto), a
// coluna rola sozinha, 30 cartões por vez, e a altura é medida a partir de onde
// o quadro começa. Não é o mesmo componente de propósito: lá as colunas,
// "ganhar abre a venda" e o selo de parado são do negócio privado; aqui as
// colunas são as etapas de etapas.ts e quem manda no cartão é o PRÓXIMO PRAZO
// do órgão.
//
// Fim de linha (Contrato assinado · Perdido · Não participamos) nasce recolhido
// e vira alvo grande no rodapé enquanto se arrasta. Quem decide se o movimento
// pode é o servidor; a tela mostra a frase dele.
import { useLayoutEffect, useRef, useState } from 'react';
import { Button, Tag, Tooltip, Typography, Skeleton } from 'antd';
import { WarningOutlined, CheckCircleOutlined, CloseOutlined, StopOutlined } from '@ant-design/icons';
import { ETAPAS, TERMINAIS, normalizar, type Etapa } from './etapas';
import {
  proximoPrazo, prontidao, valorDaLicitacao, ordenarLicitacoes, diasNaEtapa, rotuloEnvio, situacaoDoOrgao, dicaDoOrgao,
  type LicitacaoPipeline, type Ordenacao,
} from './saudeLicitacao';
import { valorCompacto } from '../../comercial-pipeline/saudeNegocio';
import { AvatarDono } from '../../comercial-pipeline/pecas';
import { fmt } from '../../comercial-pipeline/utils';
import { fitTecnicoUI } from '../fitTecnico';

const { Text } = Typography;

const LOTE = 30;

const CSS = `
.lk-quadro { display: grid; gap: 10px; align-items: start; overflow-x: auto; padding-bottom: 6px; }
.lk-col { display: flex; flex-direction: column; min-width: 0; max-height: var(--lk-alt, max(420px, calc(100vh - 190px))); }
.lk-cab { flex: none; margin-right: -10px; padding: 7px 22px 7px 24px; background: var(--lk-fundo);
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%);
  transition: background .12s; }
.lk-col:first-child .lk-cab { padding-left: 12px; border-radius: 6px 0 0 6px;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%); }
.lk-cab-n { font-size: 12px; color: #0E1433; margin-top: 1px; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; font-variant-numeric: tabular-nums; }
.lk-corpo { flex: 1 1 auto; overflow-y: auto; margin-top: 6px; padding: 8px; display: flex; flex-direction: column;
  gap: 6px; min-height: 90px; background: #F4F5F8; border: 1px solid #E6E8EF; border-radius: 8px;
  transition: background .12s, border-color .12s; }
.lk-col.lk-alvo .lk-corpo { background: #EAF0FF; border-color: #0930D0; }
.lk-cartao { position: relative; background: #fff; border: 1px solid #E6E8EF; border-left: 3px solid var(--lk-faixa, #C5C9D6);
  border-radius: 6px; padding: 8px 9px 7px; cursor: pointer; transition: box-shadow .12s, border-color .12s; }
.lk-cartao:hover { box-shadow: 0 2px 8px rgba(14,20,51,.10); border-color: #C9CEDD; border-left-color: var(--lk-faixa, #C5C9D6); }
.lk-cartao:focus-visible { outline: 2px solid #0930D0; outline-offset: 1px; }
.lk-cartao.lk-arrastando { opacity: .45; }
.lk-edital { font-size: 12.5px; font-weight: 700; color: #0E1433; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lk-orgao { font-size: 11px; color: #5A6378; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lk-objeto { font-size: 11px; color: #3C4257; line-height: 1.3; margin-top: 3px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.lk-rodape { display: flex; align-items: center; gap: 8px; margin-top: 6px; padding-top: 6px; border-top: 1px solid #F0F1F5; flex-wrap: wrap; font-size: 11px; }
.lk-fechada { cursor: pointer; min-height: 300px; border-radius: 8px; display: flex; flex-direction: column;
  align-items: center; padding: 10px 4px; border: 1px solid #E6E8EF; background: #FAFBFD; transition: background .12s; }
.lk-fechada.lk-alvo { background: #EAF0FF; border-color: #0930D0; }
.lk-soltar { position: fixed; left: 50%; bottom: 18px; transform: translateX(-50%); z-index: 1000; display: flex; gap: 10px;
  padding: 8px; border-radius: 10px; background: #0E1433; box-shadow: 0 8px 28px rgba(14,20,51,.35); }
.lk-soltar > div { min-width: 170px; padding: 12px 16px; border-radius: 7px; border: 1.5px dashed rgba(255,255,255,.45);
  color: #fff; font-size: 13px; font-weight: 600; text-align: center; transition: background .1s; }
.lk-soltar > div.lk-sobre { border-style: solid; }
@media (max-width: 768px) { .lk-soltar > div { min-width: 110px; padding: 10px; font-size: 12px; } }
`;

export function KanbanLicitacoes({ lista, loading, ordenacao, nomes, podeOperar, onAbrir, onMover }: {
  lista: LicitacaoPipeline[];
  loading: boolean;
  ordenacao: Ordenacao;
  /** e-mail → nome, para o avatar do responsável. */
  nomes: Record<string, string>;
  podeOperar: boolean;
  onAbrir: (l: LicitacaoPipeline) => void;
  onMover: (l: LicitacaoPipeline, para: string) => void;
}) {
  const [arrastando, setArrastando] = useState<LicitacaoPipeline | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  const [visiveis, setVisiveis] = useState<Record<string, number>>({});
  const [colapsadas, setColapsadas] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('lic_colunas_colapsadas');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* vale o padrão */ }
    return new Set(TERMINAIS.map(t => t.codigo));
  });
  const toggleColapsada = (c: string) => setColapsadas(prev => {
    const n = new Set(prev);
    if (n.has(c)) n.delete(c); else n.add(c);
    try { localStorage.setItem('lic_colunas_colapsadas', JSON.stringify(Array.from(n))); } catch { /* sem storage */ }
    return n;
  });

  const quadro = useRef<HTMLDivElement>(null);
  const [altura, setAltura] = useState<number | null>(null);
  useLayoutEffect(() => {
    const medir = () => {
      const el = quadro.current;
      if (!el) return;
      const topo = el.getBoundingClientRect().top + window.scrollY;
      setAltura(Math.max(420, Math.round(window.innerHeight - topo - 28)));
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [loading, lista.length]);

  const soltar = (para: string) => {
    if (arrastando) onMover(arrastando, para);
    setArrastando(null); setHoverCol(null);
  };
  const alvo = (codigo: string) => podeOperar ? ({
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); if (hoverCol !== codigo) setHoverCol(codigo); },
    onDragLeave: () => setHoverCol(h => (h === codigo ? null : h)),
    onDrop: (e: React.DragEvent) => { e.preventDefault(); soltar(codigo); },
  }) : {};

  if (loading && !lista.length) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 10 }}>
        {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} active paragraph={{ rows: 5 }} />)}
      </div>
    );
  }

  const colunas: Etapa[] = [...ETAPAS, ...TERMINAIS];
  const gridCols = colunas.map(c => colapsadas.has(c.codigo) ? '44px' : 'minmax(226px, 1fr)').join(' ');

  return (
    <>
      <style>{CSS}</style>
      <div ref={quadro} className="lk-quadro"
        style={{ gridTemplateColumns: gridCols, ['--lk-alt' as any]: altura ? `${altura}px` : undefined }}>
        {colunas.map(col => {
          const itens = ordenarLicitacoes(lista.filter(l => normalizar(l.status_interno) === col.codigo), ordenacao);
          const total = itens.reduce((s, l) => s + valorDaLicitacao(l), 0);
          const ehAlvo = hoverCol === col.codigo;

          if (colapsadas.has(col.codigo)) {
            return (
              <div key={col.codigo} {...alvo(col.codigo)} onClick={() => toggleColapsada(col.codigo)}
                role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleColapsada(col.codigo); }}
                title={`Expandir ${col.rotulo} (${itens.length} · ${fmt(total)})`}
                className={'lk-fechada' + (ehAlvo ? ' lk-alvo' : '')}>
                <Text style={{ fontSize: 14, color: col.cor }}>›</Text>
                <div style={{ marginTop: 8, writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: col.cor,
                  fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                  {col.rotulo} ({itens.length})
                </div>
              </div>
            );
          }

          const limite = visiveis[col.codigo] || LOTE;
          const mostrados = itens.slice(0, limite);
          const restantes = itens.length - mostrados.length;

          return (
            <div key={col.codigo} {...alvo(col.codigo)} className={'lk-col' + (ehAlvo ? ' lk-alvo' : '')}
              style={{ ['--lk-fundo' as any]: ehAlvo ? '#DCE5FB' : col.fundo }}>
              <div className="lk-cab">
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                  <Text strong ellipsis style={{ color: col.cor, fontSize: 12.5, minWidth: 0, flex: 1 }}>{col.rotulo}</Text>
                  <Button size="small" type="text" onClick={() => toggleColapsada(col.codigo)} title="Recolher coluna"
                    style={{ fontSize: 12, color: col.cor, padding: '0 4px', height: 18, lineHeight: 1 }}>‹</Button>
                </div>
                <Tooltip title={itens.length === 0 ? 'Nenhuma licitação nesta etapa'
                  : `${itens.length} ${itens.length === 1 ? 'licitação' : 'licitações'} · valor estimado ${fmt(total)}`}>
                  <div className="lk-cab-n">
                    <b>{itens.length}</b>
                    {total > 0 && <span style={{ color: '#5A6378' }}> · {valorCompacto(total)}</span>}
                  </div>
                </Tooltip>
              </div>
              <div className="lk-corpo">
                {itens.length === 0 && (
                  <Text type="secondary" italic style={{ fontSize: 11, textAlign: 'center', padding: '22px 4px' }}>
                    {arrastando ? 'Solte aqui' : 'Nenhuma licitação nesta etapa'}
                  </Text>
                )}
                {mostrados.map(l => (
                  <Cartao key={l.id} l={l} nome={nomes[String(l.responsavel_email || '').toLowerCase()]}
                    arrastando={arrastando?.id === l.id} podeArrastar={podeOperar}
                    onDragStart={() => setArrastando(l)}
                    onDragEnd={() => { setArrastando(null); setHoverCol(null); }}
                    onAbrir={() => onAbrir(l)} />
                ))}
                {restantes > 0 && (
                  <Button size="small" block onClick={() => setVisiveis(v => ({ ...v, [col.codigo]: limite + LOTE }))}>
                    Mostrar mais {Math.min(LOTE, restantes)} · faltam {restantes}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {arrastando != null && (
        <div className="lk-soltar" role="group" aria-label="Solte para fechar a licitação">
          <div {...alvo('nao_participamos')} className={hoverCol === 'nao_participamos' ? 'lk-sobre' : ''}
            style={hoverCol === 'nao_participamos' ? { background: '#5A6378', borderColor: '#5A6378' } : undefined}>
            <StopOutlined /> Não participamos
          </div>
          <div {...alvo('perdido')} className={hoverCol === 'perdido' ? 'lk-sobre' : ''}
            style={hoverCol === 'perdido' ? { background: '#B91626', borderColor: '#B91626' } : undefined}>
            <CloseOutlined /> Perdido
          </div>
          <div {...alvo('ganho')} className={hoverCol === 'ganho' ? 'lk-sobre' : ''}
            style={hoverCol === 'ganho' ? { background: '#0a7f4a', borderColor: '#0a7f4a' } : undefined}>
            <CheckCircleOutlined /> Contrato assinado
          </div>
        </div>
      )}
    </>
  );
}


function Cartao({ l, nome, arrastando, podeArrastar, onDragStart, onDragEnd, onAbrir }: {
  l: LicitacaoPipeline; nome?: string; arrastando: boolean; podeArrastar: boolean;
  onDragStart: () => void; onDragEnd: () => void; onAbrir: () => void;
}) {
  const prazo = proximoPrazo(l);
  const envio = rotuloEnvio(l);
  const pronta = prontidao(l);
  const dias = diasNaEtapa(l);
  const terminal = ['ganho', 'perdido', 'nao_participamos'].includes(normalizar(l.status_interno));
  // Suspensa/revogada pelo órgão (07/10/2026): no lugar de "proposta vencida".
  const orgao = terminal ? null : situacaoDoOrgao(l);
  // A faixa lateral é a cor do PRÓXIMO PRAZO: vermelho (≤ 3 dias ou vencido),
  // âmbar (≤ 7), verde (≤ 30), cinza (mais longe ou sem prazo). Licitação
  // suspensa fica âmbar; revogada/anulada, vermelha.
  const faixa = terminal ? '#C5C9D6' : orgao ? orgao.cor : (prazo ? prazo.classe.cor : '#C5C9D6');
  const alteracoes = Number(l.eventos_unack_count) || 0;
  const local = [l.cidade, l.uf].filter(Boolean).join('/');

  return (
    <div className={'lk-cartao' + (arrastando ? ' lk-arrastando' : '')}
      style={{ ['--lk-faixa' as any]: faixa }}
      draggable={podeArrastar} onDragStart={onDragStart} onDragEnd={onDragEnd}
      onClick={onAbrir} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onAbrir(); }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <div className="lk-edital" title={l.numero_edital || ''} style={{ flex: 1 }}>{l.numero_edital || 'Edital sem número'}</div>
        {(l.fit_score != null || l.fit_tecnico_nivel) && (
          <Tooltip title={`Fit técnico ${fitTecnicoUI(l).completo} · ${fitTecnicoUI(l).dica}`}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: fitTecnicoUI(l).color,
              borderRadius: 4, padding: '0 5px', lineHeight: '15px' }}>{fitTecnicoUI(l).label}</span>
          </Tooltip>
        )}
      </div>
      <div className="lk-orgao" title={`${l.orgao || ''}${local ? ' · ' + local : ''}`}>
        {l.orgao || <em>órgão não informado</em>}{local ? ` · ${local}` : ''}
      </div>
      <div className="lk-objeto" title={l.objeto || ''}>{l.objeto}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#020D7F', fontVariantNumeric: 'tabular-nums' }}>
          {valorDaLicitacao(l) > 0 ? valorCompacto(valorDaLicitacao(l)) : <span style={{ color: '#9AA0B4', fontWeight: 400 }}>sem valor</span>}
        </span>
        {l.modalidade && <Tag style={{ fontSize: 9, margin: 0, padding: '0 4px', lineHeight: '14px', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.modalidade}</Tag>}
        {!!l.gemeas?.length && (
          <Tooltip title="A mesma licitação veio pelo PNCP e pela ConLicitação; as duas foram juntadas e vale o dado mais atualizado de cada uma.">
            <Tag color="geekblue" style={{ fontSize: 9, margin: 0, padding: '0 4px', lineHeight: '14px' }}>{l.gemeas.length + 1} fontes</Tag>
          </Tooltip>
        )}
        <span style={{ marginLeft: 'auto' }}><AvatarDono nome={nome || l.responsavel_email || null} tamanho={20} /></span>
      </div>

      <div className="lk-rodape">
        {orgao && (
          <Tooltip title={dicaDoOrgao(orgao, l.prazo_efetivo)}>
            <span style={{ color: orgao.cor, fontWeight: 700, whiteSpace: 'nowrap' }}>
              {orgao.tipo === 'suspensa' ? '⛔ ' : '✖ '}{orgao.texto}
            </span>
          </Tooltip>
        )}
        {terminal ? (
          <span style={{ color: '#5A6378' }}>{l.motivo_perda || l.decisao_motivo || (normalizar(l.status_interno) === 'ganho' ? `contratado ${valorCompacto(valorDaLicitacao(l))}` : '')}</span>
        ) : prazo ? (
          <Tooltip title={`${prazo.rotulo}: ${prazo.classe.data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}>
            <span style={{ color: prazo.classe.cor, fontWeight: prazo.classe.bold ? 700 : 400, whiteSpace: 'nowrap' }}>
              {prazo.classe.icone}{prazo.rotulo} {prazo.classe.rotulo}
            </span>
          </Tooltip>
        ) : envio ? (
          <Tooltip title={l.proposta_protocolo ? `Protocolo: ${l.proposta_protocolo}` : 'Envio registrado no botão "Enviamos a proposta"'}>
            <span style={{ color: '#0a7f4a', fontWeight: 600, whiteSpace: 'nowrap' }}><CheckCircleOutlined /> {envio}</span>
          </Tooltip>
        ) : orgao ? null : (
          <span style={{ color: '#9AA0B4' }}>sem prazo</span>
        )}
        {!terminal && alteracoes > 0 && (
          <Tooltip title={l.ultimo_evento_resumo || 'Alteração do edital que ninguém marcou como vista'}>
            <span style={{ color: '#B91626', fontWeight: 600, whiteSpace: 'nowrap' }}>
              <WarningOutlined /> {alteracoes} {alteracoes === 1 ? 'alteração' : 'alterações'}
            </span>
          </Tooltip>
        )}
        {!terminal && pronta && (
          <Tooltip title={`Habilitação: ${pronta.completos} de ${pronta.total} itens prontos`}>
            <span style={{ color: pronta.pronta ? '#0a7f4a' : '#5A6378', whiteSpace: 'nowrap' }}>
              <CheckCircleOutlined /> {pronta.texto}
            </span>
          </Tooltip>
        )}
        {dias != null && dias >= 14 && !terminal && (
          <span style={{ color: '#9AA0B4', marginLeft: 'auto', whiteSpace: 'nowrap' }}>há {dias} d na etapa</span>
        )}
      </div>
    </div>
  );
}
